import React from "react";
import { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tag, X, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Product {
  id: string;
  name: string;
  image_url: string | null;
  price: string | null;
  retailer_id: string | null;
}

interface SelectedProduct extends Product {
  slotIndex: number;
}

interface ProductTagsPopoverProps {
  value: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  catalogId?: string | null;
  userId?: string;
}

export function ProductTagsPopover({ value, onChange, disabled, catalogId, userId }: ProductTagsPopoverProps) {
  const [open, setOpen] = useState(false);

  // Filter out empty strings to get actual filled product IDs
  const filledTags = (value || []).filter((t) => t && t.trim());
  const filledCount = filledTags.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-full text-xs justify-start gap-2" disabled={disabled}>
          <Tag className="h-3 w-3" />
          <span>{filledCount === 0 ? "Add product IDs" : `${filledCount} tag${filledCount !== 1 ? "s" : ""}`}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-[300px] w-[340px] p-3" align="start">
        {!catalogId ? (
          <div className="text-xs text-muted-foreground text-center py-4">Select a catalog first</div>
        ) : (
          <ProductSlotsEditor catalogId={catalogId} userId={userId} value={filledTags} onChange={onChange} />
        )}
      </PopoverContent>
    </Popover>
  );
}

// ─── Slots Editor ───────────────────────────────────────────────

interface ProductSlotsEditorProps {
  catalogId: string;
  userId?: string;
  value: string[];
  onChange: (tags: string[]) => void;
}

function ProductSlotsEditor({ catalogId, userId, value, onChange }: ProductSlotsEditorProps) {
  // Local map to remember product details for display (productId → Product)
  const [productMap, setProductMap] = useState<Record<string, Product>>({});

  // Number of visible slots: filled count + 1 (for next empty), capped at 5
  const visibleSlots = Math.min(value.length + 1, 5);

  const handleSelect = (slotIndex: number, product: Product) => {
    setProductMap((prev) => ({ ...prev, [product.id]: product }));
    const updated = [...value];
    if (slotIndex < updated.length) {
      updated[slotIndex] = product.id;
    } else {
      updated.push(product.id);
    }
    onChange(updated);

    // Fire-and-forget: upsert to recent_product_tags (uses userId for direct DB write)
    if (userId && catalogId) {
      supabase
        .from("recent_product_tags")
        .upsert(
          {
            user_id: userId,
            catalog_id: catalogId,
            product_id: product.id,
            retailer_id: product.retailer_id,
            name: product.name,
            image_url: product.image_url,
            price: product.price,
            last_used_at: new Date().toISOString(),
          },
          { onConflict: "user_id,catalog_id,product_id" },
        )
        .then(() => {});
    }
  };

  const handleRemove = (slotIndex: number) => {
    const updated = value.filter((_, i) => i !== slotIndex);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="text-xs font-medium text-muted-foreground">Product Tags (up to 5)</div>
      {Array.from({ length: visibleSlots }).map((_, index) => (
        <ProductSlot
          key={index}
          index={index}
          productId={value[index] || null}
          productInfo={value[index] ? productMap[value[index]] : undefined}
          catalogId={catalogId}
          userId={userId}
          onSelect={(product) => handleSelect(index, product)}
          onRemove={() => handleRemove(index)}
        />
      ))}
    </div>
  );
}

// ─── Single Product Slot ────────────────────────────────────────

interface ProductSlotProps {
  index: number;
  productId: string | null;
  productInfo?: Product;
  catalogId: string;
  userId?: string;
  onSelect: (product: Product) => void;
  onRemove: () => void;
}

function ProductSlot({ index, productId, productInfo, catalogId, userId, onSelect, onRemove }: ProductSlotProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const searchProducts = useCallback(
    async (searchQuery: string) => {
      if (!catalogId) return;
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke<{ products: Product[] }>("search-products", {
          body: { catalog_id: catalogId, query: searchQuery },
        });
        if (!error && data?.products) {
          setResults(data.products);
        } else {
          setResults([]);
        }
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [catalogId],
  );

  const handleQueryChange = (val: string) => {
    setQuery(val);
    setShowDropdown(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchProducts(val);
    }, 200);
  };

  const handleFocus = () => {
    setShowDropdown(true);
    if (results.length === 0 && !loading) {
      searchProducts(query);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // If this slot has a selected product, show it
  if (productId) {
    return (
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Product {index + 1}</Label>
        <div className="flex items-center gap-2 h-8 px-2 rounded-md border bg-muted/30">
          <div className="flex-1 min-w-0">
            <span className="text-xs truncate block">{productInfo?.name || productId}</span>
            <span className="text-[10px] text-muted-foreground truncate block">{productId}</span>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  }

  // Empty slot — show search input
  return (
    <div className="space-y-1" ref={containerRef}>
      <Label className="text-xs text-muted-foreground">Product {index + 1}</Label>
      <div className="relative">
        <Input
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onFocus={handleFocus}
          placeholder="Search products..."
          className="h-8 text-xs pr-8"
        />
        {loading && (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-muted-foreground" />
        )}

        {showDropdown && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border bg-popover shadow-md max-h-[200px] overflow-y-auto">
            {loading && results.length === 0 ? (
              <div className="p-2 space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : results.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground text-center">No products found</div>
            ) : (
              results.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-accent transition-colors"
                  onClick={() => {
                    onSelect(product);
                    setShowDropdown(false);
                    setQuery("");
                    setResults([]);
                  }}
                >
                  {product.image_url ? (
                    <img src={product.image_url} alt="" className="h-8 w-8 rounded-md object-cover shrink-0" />
                  ) : (
                    <div className="h-8 w-8 rounded-md bg-muted shrink-0" />
                  )}
                  <span className="text-xs truncate flex-1">{product.name}</span>
                  {product.price && <span className="text-xs text-muted-foreground shrink-0">{product.price}</span>}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
